import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';

const Auth = ({ setUser, user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: email sent, 3: new password (from link)
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [resetTokenEmail, setResetTokenEmail] = useState('');
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);
  const [submittedRestaurant, setSubmittedRestaurant] = useState('');
  const [hasPendingReview, setHasPendingReview] = useState(false);
  
  // reCAPTCHA state
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, site_key: null });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  const recaptchaRendered = useRef(false);

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get('reset_token');
    if (token) {
      // Verify token validity
      const verifyToken = async () => {
        try {
          const response = await axios.post(`${API}/auth/verify-reset-token`, { token });
          if (response.data.valid) {
            setResetToken(token);
            setResetTokenEmail(response.data.email);
            setShowForgotPassword(true);
            setForgotPasswordStep(3); // Go directly to password reset form
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
          // Clear the URL parameter
          window.history.replaceState({}, document.title, '/auth');
        }
      };
      verifyToken();
    }
  }, [searchParams]);

  // Fetch reCAPTCHA config on mount
  useEffect(() => {
    const fetchRecaptchaConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/recaptcha-config`);
        setRecaptchaConfig(response.data);
        
        // Load reCAPTCHA script if enabled and not already loaded
        if (response.data.enabled && response.data.site_key) {
          // Check if script already exists
          const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
          
          if (!existingScript && !window.grecaptcha) {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
            script.async = true;
            script.defer = true;
            
            // Define callback for when script loads
            window.onRecaptchaLoad = () => {
              if (recaptchaRef.current && !recaptchaRendered.current) {
                try {
                  recaptchaRendered.current = true;
                  recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                    sitekey: response.data.site_key,
                    callback: (token) => setRecaptchaToken(token),
                    'expired-callback': () => setRecaptchaToken(null)
                  });
                } catch (e) {
                  console.log('reCAPTCHA already rendered');
                }
              }
            };
            
            document.head.appendChild(script);
          } else if (window.grecaptcha && window.grecaptcha.render) {
            // Script already loaded, render directly if not already rendered
            if (recaptchaRef.current && !recaptchaRendered.current) {
              window.grecaptcha.ready(() => {
                try {
                  recaptchaRendered.current = true;
                  recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
                    sitekey: response.data.site_key,
                    callback: (token) => setRecaptchaToken(token),
                    'expired-callback': () => setRecaptchaToken(null)
                  });
                } catch (e) {
                  console.log('reCAPTCHA already rendered');
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch reCAPTCHA config:', error);
      }
    };
    fetchRecaptchaConfig();
    
    return () => {
      // Reset on unmount so it can render again if component remounts
      recaptchaRendered.current = false;
      recaptchaWidgetId.current = null;
    };
  }, []);

  // Check for pending review on mount
  useEffect(() => {
    const pendingReview = sessionStorage.getItem('pendingReview');
    if (pendingReview) {
      setHasPendingReview(true);
    }
  }, []);

  // Function to submit pending review
  const submitPendingReview = async (token) => {
    const pendingReviewData = sessionStorage.getItem('pendingReview');
    if (!pendingReviewData) return false;

    try {
      const reviewData = JSON.parse(pendingReviewData);
      
      // Submit the review with the new token
      await axios.post(`${API}/reviews`, {
        restaurant_name: reviewData.restaurant_name,
        address: reviewData.address,
        phone: reviewData.phone,
        city: reviewData.city,
        state: reviewData.state,
        overall_rating: reviewData.overall_rating,
        general_condition: parseInt(reviewData.general_condition),
        supplies: parseInt(reviewData.supplies),
        maintenance: parseInt(reviewData.maintenance),
        restroom_types: reviewData.restroom_types,
        comments: reviewData.comments,
        fun_url: reviewData.fun_url || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Clear pending review and show success
      sessionStorage.removeItem('pendingReview');
      setSubmittedRestaurant(reviewData.restaurant_name);
      setShowReviewSuccess(true);
      return true;
    } catch (error) {
      console.error('Failed to submit pending review:', error);
      toast.error('Failed to submit your review. Please try again from the home page.');
      sessionStorage.removeItem('pendingReview');
      return false;
    }
  };

  // Handle Google OAuth callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setProcessingGoogle(true);
      handleGoogleCallback(sessionId);
    } else if (user && !showReviewSuccess) {
      // If user is logged in, no session_id, and not showing success dialog, redirect to home
      navigate('/');
    }
  }, [searchParams, user, navigate, showReviewSuccess]);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId
      });
      
      const token = response.data.access_token;
      localStorage.setItem('token', token);
      if (setUser) setUser(response.data.user);
      toast.success(`Welcome, ${response.data.user.name}!`);
      
      // Clear the session_id from URL
      window.history.replaceState({}, document.title, '/auth');
      
      // Check for pending review and submit it
      const reviewSubmitted = await submitPendingReview(token);
      
      if (!reviewSubmitted) {
        // No pending review, redirect to home
        navigate('/');
      }
      // If review was submitted, the success dialog will show
      setProcessingGoogle(false);
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.detail || 'Google authentication failed');
      // Clear the session_id from URL
      window.history.replaceState({}, document.title, '/auth');
      setProcessingGoogle(false);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while processing Google auth
  if (processingGoogle) {
    return (
      <div className="min-h-screen toilet-humor-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Processing Google Login...
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check reCAPTCHA if enabled
    if (recaptchaConfig.enabled && !recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password, recaptcha_token: recaptchaToken }
        : { ...formData, recaptcha_token: recaptchaToken };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      const token = response.data.access_token;
      localStorage.setItem('token', token);
      toast.success(isLogin ? 'Welcome back!' : 'Account created!');
      
      // Check for pending review and submit it BEFORE setting user
      // This prevents the redirect from happening before we show the success dialog
      const reviewSubmitted = await submitPendingReview(token);
      
      // Now set the user after review submission is handled
      if (setUser) setUser(response.data.user);
      
      if (!reviewSubmitted) {
        // No pending review, redirect to home
        window.location.href = '/';
      }
      // If review was submitted, the success dialog will show (and prevent redirect)
    } catch (error) {
      toast.error(error.response?.data?.detail || `${isLogin ? 'Login' : 'Registration'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordEmailSubmit = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, a reset link has been sent.');
      setForgotPasswordStep(2); // Show "email sent" confirmation
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successful! You can now login.');
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setForgotEmail('');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken(null);
      setResetTokenEmail('');
      setIsLogin(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, '/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen toilet-humor-bg flex items-center justify-center px-4 py-12">
      {/* Full Width Header Image */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b-4 border-black overflow-hidden">
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_loo-tracker/artifacts/2igfkpva_Background%20for%20APP%20Home%20Screen%20no%20head.jpg" 
            alt="The Loo Report™ - Know Before You Go!"
            className="w-full h-auto object-contain"
            style={{
              maxHeight: '147px',
              display: 'block'
            }}
          />
          <span className="absolute bottom-1 right-2 text-[14px] font-bold text-black" style={{ fontFamily: 'Arial, sans-serif' }}>™</span>
        </div>
      </div>

      {/* Review Success Dialog */}
      <Dialog open={showReviewSuccess} onOpenChange={(open) => {
        if (!open) {
          setShowReviewSuccess(false);
          navigate('/dashboard');
        }
      }}>
        <DialogContent className="bg-white border-4 border-black rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-green-600 font-bold flex items-center gap-2" 
                         style={{ 
                           fontFamily: 'Fredoka, sans-serif',
                           letterSpacing: '0.08em',
                           textTransform: 'uppercase'
                         }}>
              <CheckCircle className="w-8 h-8" />
              Review Submitted!
            </DialogTitle>
            <DialogDescription className="text-gray-700 text-base font-semibold mt-4" 
                               style={{ 
                                 fontFamily: 'Fredoka, sans-serif',
                                 letterSpacing: '0.02em'
                               }}>
              Your review for <span className="text-red-600 font-bold">{submittedRestaurant}</span> has been successfully submitted!
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-3">
            <Button
              onClick={() => {
                setShowReviewSuccess(false);
                navigate('/dashboard');
              }}
              className="w-full py-4 bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black border-4 border-black font-bold"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: '1.25rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              View All Reviews
            </Button>
            <Button
              onClick={() => {
                setShowReviewSuccess(false);
                navigate('/');
              }}
              variant="outline"
              className="w-full py-4 border-2 border-black text-black font-bold"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: '1rem',
                letterSpacing: '0.05em'
              }}
            >
              Submit Another Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="w-full max-w-md pt-32">
        {/* Home Button */}
        <div className="flex justify-center mb-4">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-2 border-black text-black font-bold bg-yellow-400 hover:bg-yellow-500"
          >
            🏠 Home
          </Button>
        </div>

        {/* Pending Review Notice */}
        {hasPendingReview && (
          <div className="mb-4 p-4 bg-gradient-to-r from-green-100 to-blue-100 border-4 border-green-600 rounded-lg">
            <p className="text-green-800 font-bold text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              📝 You have a review ready to submit!
            </p>
            <p className="text-green-700 text-sm text-center mt-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Login or create an account to complete your submission
            </p>
          </div>
        )}
        
        {/* Title - Enhanced legibility */}
        <div className="text-center mb-8">
          {isLogin ? (
            <p className="text-4xl text-red-600 font-bold" 
               style={{ 
                 fontFamily: 'Fredoka, sans-serif',
                 letterSpacing: '0.08em',
                 textTransform: 'uppercase'
               }}
               data-testid="auth-subtitle">
              Welcome Loo Reporters
            </p>
          ) : (
            <p className="text-4xl text-red-600 font-bold" 
               style={{ 
                 fontFamily: 'Fredoka, sans-serif',
                 letterSpacing: '0.08em',
                 textTransform: 'uppercase'
               }}
               data-testid="auth-subtitle">
              Join the Loo Crew
            </p>
          )}
        </div>

        {/* Form */}
        <div className="review-card bg-white" data-testid="auth-form-container">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-red-600 text-lg font-bold" 
                       style={{ 
                         fontFamily: 'Fredoka, sans-serif',
                         letterSpacing: '0.08em',
                         textTransform: 'uppercase'
                       }}>
                  Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="mt-2 bg-white border-2 border-black text-black text-base font-bold data-entry"
                  style={{ letterSpacing: '0.8px' }}
                  placeholder="Your name"
                  data-testid="auth-name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-red-600 text-lg font-bold"
                     style={{ 
                       fontFamily: 'Fredoka, sans-serif',
                       letterSpacing: '0.08em',
                       textTransform: 'uppercase'
                     }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-2 bg-white border-2 border-black text-black text-base font-bold data-entry"
                style={{ letterSpacing: '0.8px' }}
                placeholder="your@email.com"
                data-testid="auth-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-red-600 text-lg font-bold"
                     style={{ 
                       fontFamily: 'Fredoka, sans-serif',
                       letterSpacing: '0.08em',
                       textTransform: 'uppercase'
                     }}>
                Password *
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="border-2 border-black bg-white text-black text-base font-bold data-entry pr-12"
                  style={{ letterSpacing: '0.8px' }}
                  placeholder="Your password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* reCAPTCHA Widget */}
            {recaptchaConfig.enabled && (
              <div className="flex justify-center my-4">
                <div ref={recaptchaRef}></div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full py-6 bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black border-4 border-black font-bold"
              disabled={loading || (recaptchaConfig.enabled && !recaptchaToken)}
              data-testid="auth-submit-btn"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                fontSize: '1.5rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              {loading ? 'PROCESSING...' : (isLogin ? 'LOGIN' : 'SIGN UP')}
            </Button>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  OR
                </span>
              </div>
            </div>
            {/* Social login buttons removed - App Store requires Apple Sign-In if Google is offered */}
            {/* Email/password login works great */}

            {isLogin && (
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-orange-600 hover:text-orange-700 font-semibold text-base"
                style={{ 
                  fontFamily: 'Fredoka, sans-serif',
                  letterSpacing: '0.02em'
                }}
                data-testid="forgot-password-btn"
              >
                Forgot your password?
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-red-600 hover:text-red-700 text-lg transition-colors font-bold"
              style={{ 
                fontFamily: 'Fredoka, sans-serif',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </form>

          {/* Forgot Password Dialog */}
          <Dialog open={showForgotPassword} onOpenChange={(open) => {
            setShowForgotPassword(open);
            if (!open) {
              // Reset state when closing
              setForgotPasswordStep(1);
              setForgotEmail('');
              setNewPassword('');
              setConfirmPassword('');
              if (!resetToken) {
                // Only clear URL if not in reset flow
              }
            }
          }}>
            <DialogContent className="bg-white border-4 border-black rounded-lg max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl text-red-600 font-bold" 
                             style={{ 
                               fontFamily: 'Fredoka, sans-serif',
                               letterSpacing: '0.08em',
                               textTransform: 'uppercase'
                             }}>
                  Reset Your Password
                </DialogTitle>
                <DialogDescription className="text-gray-700 text-base font-semibold" 
                                   style={{ 
                                     fontFamily: 'Fredoka, sans-serif',
                                     letterSpacing: '0.02em'
                                   }}>
                  {forgotPasswordStep === 1 && "Enter your email to receive a reset link"}
                  {forgotPasswordStep === 2 && "Check your email for the reset link"}
                  {forgotPasswordStep === 3 && `Create a new password for ${resetTokenEmail}`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {forgotPasswordStep === 1 && (
                  <>
                    <div>
                      <Label htmlFor="forgot-email" className="text-red-600 text-base font-bold"
                             style={{ 
                               fontFamily: 'Fredoka, sans-serif',
                               letterSpacing: '0.08em',
                               textTransform: 'uppercase'
                             }}>
                        Email Address
                      </Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="mt-2 border-2 border-black bg-white text-black text-base font-bold data-entry"
                        placeholder="your.email@example.com"
                        data-testid="forgot-email-input"
                      />
                    </div>
                    <Button
                      onClick={handleForgotPasswordEmailSubmit}
                      disabled={loading}
                      className="w-full text-lg py-4 bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black border-4 border-black font-bold"
                      data-testid="send-reset-link-btn"
                      style={{
                        fontFamily: 'Fredoka, sans-serif',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </>
                )}

                {forgotPasswordStep === 2 && (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="text-gray-700 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      We've sent a password reset link to <strong>{forgotEmail}</strong>
                    </p>
                    <p className="text-gray-500 text-sm mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      The link will expire in 1 hour. Check your spam folder if you don't see it.
                    </p>
                    <Button
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full text-lg py-4 bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black border-4 border-black font-bold"
                      style={{
                        fontFamily: 'Fredoka, sans-serif',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Got It
                    </Button>
                  </div>
                )}

                {forgotPasswordStep === 3 && (
                  <>
                    <div>
                      <Label htmlFor="new-password" className="text-red-600 text-base font-bold"
                             style={{ 
                               fontFamily: 'Fredoka, sans-serif',
                               letterSpacing: '0.08em',
                               textTransform: 'uppercase'
                             }}>
                        New Password (min 6 characters)
                      </Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="mt-2 border-2 border-black bg-white text-black text-base font-bold data-entry"
                        placeholder="Enter new password"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password" className="text-red-600 text-base font-bold"
                             style={{ 
                               fontFamily: 'Fredoka, sans-serif',
                               letterSpacing: '0.08em',
                               textTransform: 'uppercase'
                             }}>
                        Confirm Password
                      </Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="mt-2 border-2 border-black bg-white text-black text-base font-bold data-entry"
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button
                      onClick={handleResetPassword}
                      disabled={loading}
                      className="w-full text-lg py-4 bg-gradient-to-r from-yellow-300 via-orange-300 to-red-300 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black border-4 border-black font-bold"
                      data-testid="reset-password-btn"
                      style={{
                        fontFamily: 'Fredoka, sans-serif',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <div className="mt-6 text-center">
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;